import React, { useState } from 'react';
import { HttpService } from 'core/services/http';
import { Utils } from 'core/utils';
import { Box, Button, Typography } from '@mui/material';
import { Building2, Phone, MapPin, Pencil, ChevronDown, ChevronUp } from 'lucide-react';

function countryDisplayLabel(country) {
    if (!country) return '';
    if (typeof country === 'string' || typeof country === 'number') return String(country);
    const localized = Utils.getDefaultName(country);
    if (localized && !localized.startsWith('#')) return localized;
    return country.code || country.name || '';
}

function CompanyProfileCard({ company, onEditClick }) {
    const [descriptionExpanded, setDescriptionExpanded] = useState(false);

    const getCompanyImage = () => {
        if (!company?.image) return '/assets/images/default-company.png';
        if (company.image.startsWith('http')) return company.image;
        if (company.image.startsWith('/')) {
            return HttpService.SiteBase + company.image;
        }
        return HttpService.SiteBase + '/' + company.image;
    };

    const truncatedDescription = company?.description 
        ? (descriptionExpanded ? company.description : company.description.substring(0, 200))
        : '';

    const needsTruncation = company?.description && company.description.length > 200;

    const fullAddress = [
        company?.address,
        company?.city,
        countryDisplayLabel(company?.country)
    ].filter(Boolean).join(', ');

    return (
        <Box
            sx={{
                borderRadius: 3, // rounded-xl equivalent
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 1, // shadow-sm equivalent
                bgcolor: 'background.paper',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' }
            }}
        >
            {/* Left: Company Image */}
            <Box
                sx={{
                    width: { xs: '100%', md: '256px' }, // w-64 = 256px
                    height: { xs: '224px', md: 'auto' }, // h-56 = 224px
                    flexShrink: 0,
                    overflow: 'hidden'
                }}
            >
                <img
                    src={getCompanyImage()}
                    alt={company?.title || 'Company'}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                    }}
                    onError={(e) => {
                        e.target.src = '/assets/images/default-company.png';
                    }}
                />
            </Box>

            {/* Right: Content */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2, // gap-4 equivalent
                    p: 3, // p-6 equivalent
                    flex: 1
                }}
            >
                {/* Top Row: Company Name, ID, Manager, Edit Button */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start'
                    }}
                >
                    <Box sx={{ flex: 1 }}>
                        <Typography
                            variant="h5"
                            sx={{
                                fontSize: '1.25rem', // text-xl
                                fontWeight: 600, // font-semibold
                                mb: 1
                            }}
                        >
                            {company?.title || 'Company Name'}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 0.5,
                                color: 'text.secondary'
                            }}
                        >
                            <Building2 size={16} />
                            <Typography variant="body2" color="text.secondary">
                                {company?.vat_number || company?.id || 'N/A'}
                            </Typography>
                        </Box>
                        {company?.manager_name && (
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Manager: {company.manager_name}
                            </Typography>
                        )}
                    </Box>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Pencil size={16} />}
                        onClick={onEditClick}
                        sx={{
                            minWidth: 'auto',
                            flexShrink: 0
                        }}
                    >
                        Edit
                    </Button>
                </Box>

                {/* Description with Show More/Less */}
                {company?.description && (
                    <Box>
                        <Typography variant="body2" color="text.secondary">
                            {truncatedDescription}
                            {needsTruncation && !descriptionExpanded && '...'}
                        </Typography>
                        {needsTruncation && (
                            <Button
                                size="small"
                                onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                                sx={{
                                    mt: 0.5,
                                    textTransform: 'none',
                                    minWidth: 'auto',
                                    p: 0.5,
                                    fontSize: '0.75rem',
                                    color: 'primary.main',
                                    '&:hover': {
                                        backgroundColor: 'transparent'
                                    }
                                }}
                                endIcon={descriptionExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            >
                                {descriptionExpanded ? 'Show less' : 'Show more'}
                            </Button>
                        )}
                    </Box>
                )}

                {/* Contact Info Section */}
                <Box
                    sx={{
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        pt: 2, // pt-4 equivalent
                        mt: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1
                    }}
                >
                    {company?.phone_number && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                                <Phone size={16} />
                            </Box>
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                {company.phone_number}
                            </Typography>
                        </Box>
                    )}
                    {fullAddress && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                <MapPin size={16} />
                            </Box>
                            <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                {fullAddress}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default CompanyProfileCard;
